import { Component, forwardRef, signal, computed, effect } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { DaySchedule, WeekDay, ScheduleRange } from '../schedule-field-form/schedule-field-form.interfaces';

@Component({
  selector: 'app-schedule-basic-field-form',
  imports: [FormsModule],
  templateUrl: './schedule-basic-field-form.html',
  styleUrl: './schedule-basic-field-form.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ScheduleBasicFieldForm),
      multi: true
    }
  ]
})
export class ScheduleBasicFieldForm implements ControlValueAccessor {
  days: any[] = [
    { key: 1, name: 'Lunes' },
    { key: 2, name: 'Martes' },
    { key: 3, name: 'Miércoles' },
    { key: 4, name: 'Jueves' },
    { key: 5, name: 'Viernes' },
    { key: 6, name: 'Sábado' },
  ];

  // State management
  value = signal<DaySchedule[]>([]);
  selectedDays = signal<Set<number>>(new Set());
  startTime = signal<string>('');
  endTime = signal<string>('');
  infoMessage = signal<string>('');

  // ControlValueAccessor callbacks
  onChange = (_: any) => { };
  onTouched = () => { };

  // Computed property to check if add button should be disabled
  isValidTimeRange = computed(() => {
    const hasStartTime = this.startTime().trim() !== '';
    const hasEndTime = this.endTime().trim() !== '';
    const validTimeRange = hasStartTime && hasEndTime && this.startTime() < this.endTime();
    return validTimeRange;
  });
  
  isAddButtonDisabled = computed(() => {
    const hasSelectedDays = this.selectedDays().size > 0;    
    return !hasSelectedDays || !this.isValidTimeRange();
  });

  effectValidTimeRange = effect(() => {
    const hasStartEndTime = this.startTime().trim() !== '' && this.endTime().trim() !== '';
    if (hasStartEndTime && !this.isValidTimeRange()) {
      this.infoMessage.set('Debe seleccionar un rango de horas válido');
    } else {
      this.infoMessage.set('');
    }
  });

  // Get days with schedules for display (sorted)
  getDaysWithSchedules = computed(() => {
    return this.value()
      .filter(day => day.schedules.length > 0)
      .sort((a, b) => a.day - b.day) // Sort by day number (1-6)
      .map(day => ({
        ...day,
        schedules: [...day.schedules].sort((a, b) => {
          // Sort schedules by start time
          return this.timeToMinutes(a.start) - this.timeToMinutes(b.start);
        })
      }));
  });


  // ControlValueAccessor implementation
  writeValue(value: DaySchedule[]): void {
    if (Array.isArray(value)) {
      this.value.set(value);
    } else {
      this.value.set([]);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Day checkbox change handler
  onDayChange(dayKey: number, checked: boolean): void {
    const current = new Set(this.selectedDays());
    if (checked) {
      current.add(dayKey);
    } else {
      current.delete(dayKey);
    }
    this.selectedDays.set(current);
  }

  // Handle start time change and auto-fill end time
  onStartTimeChange(newStartTime: string): void {
    this.startTime.set(newStartTime);
    
    // If end time is empty, auto-fill with start time value
    if (newStartTime && !this.endTime()) {
      // set enTime + 1 hour
      const [hours] = newStartTime.split(':').map(Number);
      const newEndTime = `${(hours + 1).toString().padStart(2, '0')}:00`;
      this.endTime.set(newEndTime);
    }
  }

  // Convert time string (HH:MM) to minutes for comparison
  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  // Check if two time ranges overlap
  private hasTimeOverlap(range1: ScheduleRange, range2: ScheduleRange): boolean {
    const start1 = this.timeToMinutes(range1.start);
    const end1 = this.timeToMinutes(range1.end);
    const start2 = this.timeToMinutes(range2.start);
    const end2 = this.timeToMinutes(range2.end);

    // Ranges overlap if one starts before the other ends
    // Adjacent ranges (e.g., 10:00-12:00 and 12:00-14:00) are NOT considered overlapping
    return start1 < end2 && start2 < end1;
  }

  // Check if schedule conflicts with existing schedules for a day
  private hasScheduleConflict(dayKey: WeekDay, newSchedule: ScheduleRange): boolean {
    const day = this.value().find(d => d.day === dayKey);
    if (!day) return false;

    return day.schedules.some(schedule => this.hasTimeOverlap(schedule, newSchedule));
  }

  // Check if exact schedule already exists for a day
  private isDuplicateSchedule(dayKey: WeekDay, newSchedule: ScheduleRange): boolean {
    const day = this.value().find(d => d.day === dayKey);
    if (!day) return false;

    return day.schedules.some(
      schedule => schedule.start === newSchedule.start && schedule.end === newSchedule.end
    );
  }

  // Clean days with empty schedules array
  private cleanEmptyDays(days: DaySchedule[]): DaySchedule[] {
    return days.filter(day => day.schedules.length > 0);
  }

  // Add schedule for selected days
  addSchedule(): void {
    this.infoMessage.set('');
    if (this.isAddButtonDisabled()) return;

    const newSchedule: ScheduleRange = {
      start: this.startTime(),
      end: this.endTime()
    };

    // 1. Validation Step: Check for conflicts without modifying
    const selectedDaysArr = Array.from(this.selectedDays());
    
    for (const dayKey of selectedDaysArr) {
      // Check for conflicts
       if (this.hasScheduleConflict(dayKey as WeekDay, newSchedule)) {
         this.infoMessage.set(`Horario existente para el dia ${this.days.find(d => d.key === dayKey)?.name}`);
         console.warn(`Schedule conflict detected for day ${dayKey}`);
         return; // STOP execution, do not clear inputs
       }
 
       // Check for duplicates
       if (this.isDuplicateSchedule(dayKey as WeekDay, newSchedule)) {
         this.infoMessage.set(`Horario duplicado para el dia ${this.days.find(d => d.key === dayKey)?.name}`);
         console.warn(`Duplicate schedule detected for day ${dayKey}`);
         return; // STOP execution, do not clear inputs
       }
    }


    // 2. Application Step: Apply changes if validation passed
    const copy = this.value().map(day => ({
      ...day,
      schedules: [...day.schedules]
    }));

    selectedDaysArr.forEach(dayKey => {
      // Find or create day entry
      let day = copy.find(d => d.day === dayKey);
      if (!day) {
        const dayInfo = this.days.find(d => d.key === dayKey);
        day = {
          day: dayKey as WeekDay,
          day_name: dayInfo?.name || '',
          schedules: []
        };
        copy.push(day);
      }

      // Add the schedule
      day.schedules.push({ ...newSchedule });
    });

    // Clean empty days and update value
    const cleaned = this.cleanEmptyDays(copy);
    this.value.set(cleaned);
    this.onChange(cleaned);

    // Clear inputs
    this.startTime.set('');
    this.endTime.set('');
    this.selectedDays.set(new Set());
  }

  // Remove a specific schedule from a day
  removeSchedule(dayKey: WeekDay, schedule: ScheduleRange): void {
    const copy = this.value().map(day => ({
      ...day,
      schedules: [...day.schedules]
    }));

    const day = copy.find(d => d.day === dayKey);
    if (day) {
      day.schedules = day.schedules.filter(
        s => !(s.start === schedule.start && s.end === schedule.end)
      );
    }

    // Clean empty days and update value
    const cleaned = this.cleanEmptyDays(copy);
    this.value.set(cleaned);
    this.onChange(cleaned);
  }

  // Check if a day is selected
  isDaySelected(dayKey: number): boolean {
    return this.selectedDays().has(dayKey);
  }
}
